import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../../services/api';
import { FetchParams } from './employeesSlice';
import { buildQueryString } from '../../utils/queryStringBuilder';

interface ClientsState {
    clients: any[];
    client: any | null;
    events: {
        data: any[];
        meta: {
            month: number;
            year: number;
            total: number;
        } | null;
    };
    isLoading: boolean;
    isLoadingEvents: boolean;
    error: string | null;
    eventsError: string | null;
    pagination: {
        page: number;
        pageSize: number;
        total: number;
    };
}

const initialState: ClientsState = {
    clients: [],
    client: null,
    events: {
        data: [],
        meta: null
    },
    isLoading: false,
    isLoadingEvents: false,
    error: null,
    eventsError: null,
    pagination: {
        page: 0,
        pageSize: 10,
        total: 0
    }
};

export const fetchClients = createAsyncThunk(
    'clients/fetchClients',
    async (params: FetchParams = {}, { rejectWithValue }) => {
        try {
            // Build query string using the utility function
            const queryString = buildQueryString(params);
            
            const response = await api.get(`/api/clients${queryString}`);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch clients');
        }
    }
);

export const fetchClient = createAsyncThunk(
    'clients/fetchClient',
    async (id: number, { rejectWithValue }) => {
        try {
            const response = await api.get(`/api/clients/${id}?populate=*`);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch client');
        }
    }
);

export const createClient = createAsyncThunk(
    'clients/createClient',
    async (clientData: any, { rejectWithValue }) => {
        try {
            const response = await api.post('/api/clients', { data: clientData });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.error?.message || 'Failed to create client');
        }
    }
);

export const updateClient = createAsyncThunk(
    'clients/updateClient',
    async ({ id, data }: { id: number; data: any }, { rejectWithValue }) => {
        try {
            const response = await api.put(`/api/clients/${id}`, { data });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.error?.message || 'Failed to update client');
        }
    }
);

export const deleteClient = createAsyncThunk(
    'clients/deleteClient',
    async (id: number, { rejectWithValue }) => {
        try {
            await api.delete(`/api/clients/${id}`);
            return id;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.error?.message || 'Failed to delete client');
        }
    }
);

export const fetchClientEvents = createAsyncThunk(
    'clients/fetchClientEvents',
    async (params: { month?: number; year?: number } = {}, { rejectWithValue }) => {
        try {
            const queryParams = new URLSearchParams();
            if (params.month) queryParams.append('month', params.month.toString());
            if (params.year) queryParams.append('year', params.year.toString());
            
            const queryString = queryParams.toString();
            const url = `/api/client/events${queryString ? `?${queryString}` : ''}`;
            
            const response = await api.get(url);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch client events');
        }
    }
);

const clientsSlice = createSlice({
    name: 'clients',
    initialState,
    reducers: {
        clearClientError: (state) => {
            state.error = null;
        },
        clearEventsError: (state) => {
            state.eventsError = null;
        },
        clearClient: (state) => {
            state.client = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch Clients
            .addCase(fetchClients.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchClients.fulfilled, (state, action: PayloadAction<any>) => {
                state.isLoading = false;
                state.clients = action.payload.data;
                if (action.payload.meta && action.payload.meta.pagination) {
                    state.pagination = {
                        page: action.payload.meta.pagination.page - 1, // Convert to 0-based for frontend
                        pageSize: action.payload.meta.pagination.pageSize,
                        total: action.payload.meta.pagination.total
                    };
                }
            })
            .addCase(fetchClients.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Fetch Client
            .addCase(fetchClient.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchClient.fulfilled, (state, action: PayloadAction<any>) => {
                state.isLoading = false;
                state.client = action.payload.data;
            })
            .addCase(fetchClient.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Create Client
            .addCase(createClient.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(createClient.fulfilled, (state, action: PayloadAction<any>) => {
                state.isLoading = false;
                state.clients.push(action.payload.data);
            })
            .addCase(createClient.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Update Client
            .addCase(updateClient.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(updateClient.fulfilled, (state, action: PayloadAction<any>) => {
                state.isLoading = false;
                const index = state.clients.findIndex(client => client.id === action.payload.data.id);
                if (index !== -1) {
                    state.clients[index] = action.payload.data;
                }
                state.client = action.payload.data;
            })
            .addCase(updateClient.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Delete Client
            .addCase(deleteClient.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(deleteClient.fulfilled, (state, action: PayloadAction<number>) => {
                state.isLoading = false;
                state.clients = state.clients.filter(client => client.id !== action.payload);
                if (state.client && state.client.id === action.payload) {
                    state.client = null;
                }
            })
            .addCase(deleteClient.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Fetch Client Events
            .addCase(fetchClientEvents.pending, (state) => {
                state.isLoadingEvents = true;
                state.eventsError = null;
            })
            .addCase(fetchClientEvents.fulfilled, (state, action: PayloadAction<any>) => {
                state.isLoadingEvents = false;
                state.events = {
                    data: action.payload.data || [],
                    meta: action.payload.meta || null
                };
            })
            .addCase(fetchClientEvents.rejected, (state, action) => {
                state.isLoadingEvents = false;
                state.eventsError = action.payload as string;
            });
    },
});

export const { clearClientError, clearEventsError, clearClient } = clientsSlice.actions;
export default clientsSlice.reducer; 